/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import colors from '@sportninja/common/constants/appColors'
import dayjs from 'dayjs'
import { Event } from '../../../../common/types/index'
import { paragraphXSmall, weightRegular } from '../css'
import { getImageThumbnailId } from '@sportninja/common/reducers/helpers'
import Text from '../ts-components/Text'
import { useState, useCallback } from 'react'
import AdditionalInfoModal from './AdditionalInfoModal'

// Helper function to truncate HTML content while preserving formatting
const truncateHtmlContent = (
  html: string,
  maxLength: number = 200
): { truncated: string; isTruncated: boolean } => {
  // Clean up excessive whitespace and empty lines
  let cleanedHtml = html
    .replace(/<br\s*\/?>/gi, ' ') // Replace <br> tags with spaces
    .replace(/\s*<\/p>\s*<p>\s*/gi, '</p><p>') // Remove whitespace between paragraphs
    .replace(/\s+/g, ' ') // Replace multiple spaces with single space
    .replace(/>\s+</g, '><') // Remove whitespace between HTML tags
    .trim()

  // Remove HTML tags to check text length
  const strippedText = cleanedHtml.replace(/<[^>]*>/g, '').trim()

  // Check if truncation is needed
  if (strippedText.length <= maxLength) {
    return { truncated: cleanedHtml, isTruncated: false }
  }

  // Find the truncation point in the original HTML
  let textLength = 0
  let truncatedHtml = ''
  let inTag = false

  for (let i = 0; i < cleanedHtml.length; i++) {
    const char = cleanedHtml[i]

    if (char === '<') {
      inTag = true
    }

    if (!inTag) {
      textLength++
    }

    truncatedHtml += char

    if (char === '>') {
      inTag = false
    }

    if (textLength >= maxLength && !inTag) {
      break
    }
  }

  // Add ellipsis
  truncatedHtml += '...'
  return { truncated: truncatedHtml, isTruncated: true }
}

type EventDetailsProps = {
  value: string
}

type EventCardProps = {
  eventInfo: Event
}

export const EventDetails = ({ value }: EventDetailsProps) => (
  <p
    css={css`
      ${paragraphXSmall}
      ${weightRegular}
      color: ${colors.WHITE};
    `}
  >
    {value}
  </p>
)

export const EventCard = ({ eventInfo }: EventCardProps) => {
  const [isModalOpen, setIsModalOpen] = useState(false)

  const handleOpenModal = useCallback(() => {
    setIsModalOpen(true)
  }, [])

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false)
  }, [])

  const imageUrl =
    getImageThumbnailId(eventInfo) ||
    '/images/generic_placeholders/placeholder_competition.png'

  const headerContent = eventInfo?.registration?.header_content
  const hasHeaderContent = headerContent && headerContent.trim().length > 0

  let truncatedContent = ''
  let isTruncated = false

  if (hasHeaderContent) {
    const result = truncateHtmlContent(headerContent, 200)
    truncatedContent = result.truncated
    isTruncated = result.isTruncated
  }

  return (
    <div
      css={css`
        background-color: ${colors.NEUTRAL_700};
        padding: 16px;
        border-radius: 16px;
        margin-top: 32px;
        display: flex;
        align-items: center;
        gap: 16px;
        color: ${colors.WHITE};
        width: 100%;
        align-self: stretch;
        @media (max-width: 768px) {
          padding: 16px;
          flex-direction: column;
        }
      `}
    >
      <div
        css={css`
          max-width: 100px;
          max-height: 100px;
          border-radius: 8px;
          border: 2px solid ${colors.WHITE};
        `}
      >
        <img
          src={imageUrl}
          css={css`
            width: 100%;
            height: 100%;
          `}
          onError={(e: any) => {
            e.target.onerror = null
            e.target.src =
              '/images/generic_placeholders/placeholder_competition.png'
          }}
        />
      </div>
      <div
        css={css`
          flex: 1;
          display: flex;
          flex-direction: column;
          gap: 4px;
        `}
      >
        <Text variant='heading3' color={colors.WHITE} weight='semiBold'>
          {eventInfo?.name || ''}
        </Text>
        <EventDetails
          value={eventInfo?.registration?.organization_name || ''}
        />
        {eventInfo?.registration?.root_schedule ? (
          <EventDetails
            value={`${dayjs(eventInfo?.registration?.root_schedule?.starts_at)
              .utc()
              .format('MMM D')} - ${dayjs(
              eventInfo?.registration?.root_schedule?.ends_at
            )
              .utc()
              .format('MMM D, YYYY')} `}
          />
        ) : null}
        {eventInfo?.registration?.location && (
          <EventDetails value={eventInfo?.location} />
        )}
      </div>

      {/* Additional Info Section */}
      {hasHeaderContent && (
        <div
          css={css`
            flex: 1;
            display: flex;
            flex-direction: column;
            gap: 8px;
            max-width: 400px;
            @media (max-width: 768px) {
              max-width: 100%;
              margin-top: 16px;
            }
          `}
        >
          <div
            css={css`
              cursor: pointer;
              &:hover {
                opacity: 0.8;
              }
            `}
            onClick={handleOpenModal}
          >
            <div
              css={css`
                text-align: right;
                color: ${colors.NEUTRAL_0};
                font-size: 14px;
                line-height: 145%;
                font-weight: 400;
                word-wrap: break-word;
                overflow-wrap: break-word;
                hyphens: auto;

                /* Remove extra whitespace and empty lines */
                br {
                  display: none;
                }

                /* Remove empty paragraphs */
                p:empty {
                  display: none;
                }

                /* Reduce excessive spacing between elements */
                * + * {
                  margin-top: 0;
                }

                /* Ensure first element has no top margin */
                *:first-child {
                  margin-top: 0;
                }

                /* Ensure last element has no bottom margin */
                *:last-child {
                  margin-bottom: 0;
                }

                /* Style the HTML content */
                h1,
                h2,
                h3,
                h4,
                h5,
                h6 {
                  color: ${colors.NEUTRAL_0};
                  margin: 4px 0;
                  font-weight: 600;
                  font-size: 14px;
                  line-height: 145%;
                }

                p {
                  color: ${colors.NEUTRAL_0};
                  margin: 2px 0;
                  font-size: 14px;
                  line-height: 145%;
                  font-weight: 400;
                }

                /* Use native list styles with proper positioning */
                ul {
                  margin: 2px 0;
                  padding-left: 20px;
                  color: ${colors.NEUTRAL_0};
                  list-style-type: disc;
                  font-size: 14px;
                  line-height: 145%;
                  font-weight: 400;
                }

                ol {
                  margin: 2px 0;
                  padding-left: 20px;
                  color: ${colors.NEUTRAL_0};
                  list-style-type: decimal;
                  font-size: 14px;
                  line-height: 145%;
                  font-weight: 400;
                }

                li {
                  margin: 1px 0;
                  color: ${colors.NEUTRAL_0};
                  font-size: 14px;
                  line-height: 145%;
                  font-weight: 400;
                }

                /* Support for nested lists */
                ul ul,
                ol ol,
                ul ol,
                ol ul {
                  margin: 1px 0;
                  padding-left: 20px;
                }

                a {
                  color: ${colors.PRIMARY};
                  text-decoration: underline;
                  font-size: 14px;
                  line-height: 145%;
                  font-weight: 400;

                  &:hover {
                    color: ${colors.PRIMARY_200};
                  }
                }

                strong {
                  color: ${colors.NEUTRAL_0};
                  font-weight: 600;
                  font-size: 14px;
                  line-height: 145%;
                }

                em {
                  color: ${colors.NEUTRAL_0};
                  font-style: italic;
                  font-size: 14px;
                  line-height: 145%;
                  font-weight: 400;
                }

                blockquote {
                  border-left: 2px solid ${colors.PRIMARY};
                  margin: 4px 0;
                  padding-left: 8px;
                  color: ${colors.NEUTRAL_100};
                  font-size: 14px;
                  line-height: 145%;
                  font-weight: 400;
                }
              `}
              dangerouslySetInnerHTML={{ __html: truncatedContent }}
            />
            {isTruncated && (
              <div
                css={css`
                  margin-top: 4px;
                  cursor: pointer;
                  text-align: right;
                  &:hover {
                    opacity: 0.8;
                  }
                `}
              >
                <Text
                  variant='paragraphSmall'
                  color={colors.PRIMARY}
                  weight='regular'
                >
                  Read More
                </Text>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Additional Info Modal */}
      {hasHeaderContent && (
        <AdditionalInfoModal
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          content={headerContent}
        />
      )}
    </div>
  )
}
